import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ExcelJS from 'exceljs';

const ShopPortabilityReport = ({
  month,
  year,
  district,
  afso,
  heading,
  showExcel = false
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${process.env.REACT_APP_URL}/api/report/shops?month=${month}&year=${year}&district=${encodeURIComponent(district)}&block=${encodeURIComponent(afso)}`)
      .then(res => {
        setData(res.data.data || []);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        alert('Error loading shop data');
        setLoading(false);
      });
  }, [month, year, district, afso]);

  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  const monthName = monthNames[parseInt(month) - 1] || '';

  if (loading) return <div>Loading Shop Report...</div>;
  if (data.length === 0) return <div align="center" className="response">No data found.</div>;

  const firstRow = data[0];
  const commodities = Object.keys(firstRow.percent_port_per_commodity || {});

  // Initialize totals
  const totals = data.reduce((acc, item) => {
    const info = item.fpsInfo || {};
    acc.totalCards += info.total_cards || 0;
    acc.saleCards += info.total_sale_rc || 0;
    acc.portCards += info.total_port_rc || 0;
    acc.outRc += info.out_rc || 0;
    acc.impdsRc += info.impds_rc || 0;

    commodities.forEach(comm => {
      acc.saleQty[comm] = (acc.saleQty[comm] || 0) + (item.salecommodities_qty[`sale_${comm}`] || 0);
      acc.portQty[comm] = (acc.portQty[comm] || 0) + (item.portcommodities_qty[`port_${comm}`] || 0);
    });

    return acc;
  }, {
    totalCards: 0,
    saleCards: 0,
    portCards: 0,
    outRc: 0,
    impdsRc: 0,
    saleQty: {},
    portQty: {}
  });

  totals.saleCardsPct = totals.totalCards > 0 ? (totals.saleCards / totals.totalCards * 100).toFixed(2) : 0;
  totals.portCardsPct = totals.saleCards > 0 ? (totals.portCards / totals.saleCards * 100).toFixed(2) : 0;

  // Commodity port %
  totals.portPct = {};
  commodities.forEach(comm => {
    const sale = totals.saleQty[comm] || 0;
    const port = totals.portQty[comm] || 0;
    totals.portPct[comm] = sale > 0 ? (port / sale * 100).toFixed(2) : 0;
  });

  const downloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Shop Portability Report');

    const totalColumns = 11 + commodities.length * 3;

    /* ===============================
       COMMON STYLES
    =============================== */
    const blackHeaderStyle = {
      fill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF000000' }
      },
      font: {
        bold: true,
        color: { argb: 'FFFFFFFF' }
      },
      alignment: {
        horizontal: 'left',
        vertical: 'middle'
      },
      border: {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      }
    };

    const leftCellStyle = {
      alignment: {
        horizontal: 'left',
        vertical: 'middle'
      },
      border: {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      }
    };

    /* ===============================
       1. MAIN HEADING
    =============================== */
    const headingRow = worksheet.addRow([
      `${heading} - ${monthName} / ${year}`
    ]);

    worksheet.mergeCells(1, 1, 1, totalColumns);

    headingRow.eachCell(cell => {
      cell.style = blackHeaderStyle;
      cell.font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
    });

    const headerRow1 = worksheet.addRow([
      'S.No', 'Shop ID', 'Shop Name',
      'Total Cards', 'Sale Cards', '% of Total Sale Cards',
      'Portability Cards', 'Portability %',
      'In RC', 'Out RC', 'IMPDS RC',
      ...Array(commodities.length).fill('Total Sale Qty (Kg)'),
      ...Array(commodities.length).fill('Portability Sale Qty (Kg)'),
      ...Array(commodities.length).fill('Portability Sale %')
    ]);

    for (let i = 1; i <= 11; i++) {
      worksheet.mergeCells(headerRow1.number, i, headerRow1.number + 1, i);
    }

    let col = 12;
    worksheet.mergeCells(headerRow1.number, col, headerRow1.number, col + commodities.length - 1);
    col += commodities.length;
    worksheet.mergeCells(headerRow1.number, col, headerRow1.number, col + commodities.length - 1);
    col += commodities.length;
    worksheet.mergeCells(headerRow1.number, col, headerRow1.number, col + commodities.length - 1);

    headerRow1.eachCell(cell => cell.style = blackHeaderStyle);

    /* ===============================
       3. HEADER ROW 2
    =============================== */
    const headerRow2 = worksheet.addRow([
      ...Array(11).fill(''),
      ...commodities.map(c => c.replace(/_/g, ' ').toUpperCase()),
      ...commodities.map(c => c.replace(/_/g, ' ').toUpperCase()),
      ...commodities.map(c => c.replace(/_/g, ' ').toUpperCase())
    ]);

    headerRow2.eachCell(cell => cell.style = blackHeaderStyle);

    /* ===============================
       4. DATA ROWS (ALL LEFT ALIGNED)
    =============================== */
    data.forEach((item, idx) => {
      const info = item.fpsInfo || {};

      const row = worksheet.addRow([
        idx + 1,
        info.fps_id,
        info.fps_name_en || 'N/A',
        info.total_cards || 0,
        info.total_sale_rc || 0,
        info.total_cards ? ((info.total_sale_rc / info.total_cards) * 100).toFixed(2) : 0,
        info.total_port_rc || 0,
        info.percent_port_against_sale?.toFixed(2) || 0,
        info.total_port_rc || 0,
        info.out_rc || 0,
        info.impds_rc || 0,
        ...commodities.map(c => item.salecommodities_qty[`sale_${c}`] || 0),
        ...commodities.map(c => item.portcommodities_qty[`port_${c}`] || 0),
        ...commodities.map(c => item.percent_port_per_commodity[c] || 0)
      ]);

      row.eachCell(cell => {
        cell.style = leftCellStyle;
      });
    });

    /* ===============================
       5. TOTAL ROW
    =============================== */
    const totalRow = worksheet.addRow([
      'TOTAL', '', '',
      totals.totalCards,
      totals.saleCards,
      totals.saleCardsPct,
      totals.portCards,
      totals.portCardsPct,
      totals.portCards,
      totals.outRc,
      totals.impdsRc,
      ...commodities.map(c => totals.saleQty[c] || 0),
      ...commodities.map(c => totals.portQty[c] || 0),
      ...commodities.map(c => totals.portPct[c] || 0)
    ]);

    worksheet.mergeCells(totalRow.number, 1, totalRow.number, 3);
    totalRow.eachCell(cell => cell.style = blackHeaderStyle);

    worksheet.columns.forEach(col => {
      col.width = 18;
    });

    worksheet.views = [{ state: 'frozen', ySplit: 4 }];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Shop_Portability_Report_${monthName}_${year}.xlsx`;
    link.click();
  };

  return (
    <div>
      <table border="1" cellPadding="5" cellSpacing="0" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#000', zIndex: 1 }}>
          <tr>
            <th colSpan={11 + commodities.length * 3} className='tableheader'>
              {heading} {district} / {afso} - {monthName} / {year}
            </th>
          </tr>
          <tr className='tableheader'>
            <th rowSpan="2">S.No</th>
            <th rowSpan="2">Shop ID</th>
            <th rowSpan="2">Shop Name</th>
            <th rowSpan="2">Total Cards</th>
            <th rowSpan="2">Sale Cards</th>
            <th rowSpan="2">% of Total Sale Cards</th>
            <th rowSpan="2">Portability Cards</th>
            <th rowSpan="2">Portability %</th>
            <th colSpan="3">Sale Cards</th>
            <th colSpan={commodities.length}>Total Sale Qty (Kg)</th>
            <th colSpan={commodities.length}>Portability Sale Qty (Kg)</th>
            <th colSpan={commodities.length}>Portability Sale %</th>
          </tr>
          <tr className="tableheader" style={{ backgroundColor: '#444' }}>
            <th>In</th>
            <th>Out</th>
            <th>IMPDS</th>
            {commodities.map(comm => <th key={comm}>{comm.replace(/_/g, ' ').toUpperCase()}</th>)}
            {commodities.map(comm => <th key={`port_${comm}`}>{comm.replace(/_/g, ' ').toUpperCase()}</th>)}
            {commodities.map(comm => <th key={`pct_${comm}`}>{comm.replace(/_/g, ' ').toUpperCase()}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.map((item, idx) => {
            const info = item.fpsInfo || {};
            return (
              <tr key={idx}>
                <td align="right">{idx + 1}</td>
                <td align="right">{info.fps_id}</td>
                <td align="left">{info.fps_name_en || 'N/A'}</td>
                <td align="right">{info.total_cards}</td>
                <td align="right">{info.total_sale_rc}</td>
                <td align="right">{((info.total_sale_rc / info.total_cards) * 100).toFixed(2)}</td>
                <td align="right">{info.total_port_rc}</td>
                <td align="right">{info.percent_port_against_sale?.toFixed(2)}</td>
                <td align="right">{info.total_port_rc || 0}</td>
                <td align="right">{info.out_rc || 0}</td>
                <td align="right">{info.impds_rc || 0}</td>
                {commodities.map(comm => (
                  <td align="right" key={comm}>{item.salecommodities_qty[`sale_${comm}`]?.toFixed(3) || 0}</td>
                ))}
                {commodities.map(comm => (
                  <td align="right" key={`port_${comm}`}>{item.portcommodities_qty[`port_${comm}`]?.toFixed(2) || 0}</td>
                ))}
                {commodities.map(comm => (
                  <td align="right" key={`pct_${comm}`}>{item.percent_port_per_commodity[comm]?.toFixed(2)}</td>
                ))}
              </tr>
            );
          })}

          {/* Totals Row */}
          <tr className='tableheader'>
            <td colSpan="3">TOTAL</td>
            <td>{totals.totalCards}</td>
            <td>{totals.saleCards}</td>
            <td>{totals.saleCardsPct}</td>
            <td>{totals.portCards}</td>
            <td>{totals.portCardsPct}</td>
            <td>{totals.portCards}</td>
            <td>{totals.outRc}</td>
            <td>{totals.impdsRc}</td>
            {commodities.map(comm => (
              <td key={comm}>{totals.saleQty[comm]?.toFixed(2) || 0}</td>
            ))}
            {commodities.map(comm => (
              <td key={`port_${comm}`}>{totals.portQty[comm]?.toFixed(2) || 0}</td>
            ))}
            {commodities.map(comm => (
              <td key={`pct_${comm}`}>{totals.portPct[comm]}</td>
            ))}
          </tr>
        </tbody>
      </table>

      <br />

      {showExcel && (
        <div style={{ textAlign: 'center', marginTop: '10px' }}>
          <button onClick={downloadExcel} className="button button2">
            Download Excel
          </button>
        </div>
      )}

      <br /><br /><br /><br />
    </div>
  );
};

export default ShopPortabilityReport;